import { EnvConfig } from '../config';
import { Environment } from '../config/enums';

/**
 * Module Federation remote URL validation.
 *
 * `src/index.tsx` honors any pre-set `window.<remote>RemoteUrl` over its
 * build-time fallback so test harnesses (Playwright, etc.) can redirect a
 * federated remote without a host rebuild. Because the resolved URL is then
 * fetched as same-origin JavaScript by webpack's federation runtime, an
 * attacker-controlled value is RCE-equivalent: any HTML-injection or DOM
 * clobbering sink that lands earlier in the page lifecycle could otherwise
 * pin a hostile origin.
 *
 * Two-layer defense:
 *
 *   1. **Prod lockdown**: in `production` builds, ANY pre-set window value is
 *      rejected. There is no legitimate runtime-override use case in prod.
 *
 *   2. **Non-prod allow-list** (stage / demo / dev): the hostname must match
 *      one of the *specific Firebase projects* we ship as remotes, or
 *      localhost. We do NOT permit "any Firebase project" — an attacker who
 *      can pin `<anything>.web.app` would gain JS execution in our origin.
 *
 * Adding a new federated remote requires explicitly extending the allow-list
 * here. That coupling is deliberate: the trust boundary should not silently
 * widen when a new remote ships.
 *
 * Firebase preview channel URL format:
 *   https://<project>--<channel>-<hash>.web.app
 */

/** Firebase Hosting site IDs we ship remotes from (prod + stage variants). */
const FIREBASE_PROJECTS = ['xsolla-money-module', 'xsolla-money-module-stage'] as const;

const ALLOWED_REMOTE_HOSTS: readonly RegExp[] = [
  // Each known project's primary domain + its preview channels.
  ...FIREBASE_PROJECTS.flatMap((p) => [new RegExp(`^${p}\\.web\\.app$`), new RegExp(`^${p}--[a-z0-9-]+-[a-z0-9]+\\.web\\.app$`)])
];

function isAllowedRemoteUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  if (parsed.protocol === 'http:') {
    return parsed.hostname === 'localhost';
  }
  if (parsed.protocol !== 'https:') return false;
  return ALLOWED_REMOTE_HOSTS.some((re) => re.test(parsed.hostname));
}

/**
 * Pick a Module Federation remote URL.
 *
 * - In `production`: always returns `fallback`. Pre-set window values are
 *   ignored regardless of content.
 * - In non-prod envs: returns `injected` only if it passes allow-list
 *   validation; otherwise `fallback`.
 *
 * The `env` parameter exists for testability and defaults to the build-time
 * env. Callers should not pass it.
 */
export function safeRemoteUrl(injected: unknown, fallback: string, env: Environment = EnvConfig.ENV): string {
  if (env === Environment.prod) {
    if (injected !== undefined && injected !== null) {
      // Surface attempted overrides in prod — should never happen legitimately.
      // eslint-disable-next-line no-console
      console.warn('[safeRemoteUrl] Ignoring injected remote URL in production build.');
    }
    return fallback;
  }

  if (isAllowedRemoteUrl(injected)) {
    return injected;
  }
  if (injected !== undefined && injected !== null) {
    // eslint-disable-next-line no-console
    console.warn('[safeRemoteUrl] Rejected injected remote URL — not on allow-list. Falling back to build-time value.');
  }
  return fallback;
}
